
import { window } from "@packages/global";
import { Inject, Injectable } from "@webmotors/ioc";

import { LOGGER, type Logger } from "../logger";
import { LOCAL_STORAGE_SERVICE } from "./local-storage.types";

@Injectable(LOCAL_STORAGE_SERVICE)
export class LocalStorageService {
  private readonly logger: Logger;

  constructor(@Inject(LOGGER) private readonly loggerFactory: Logger) {
    this.logger = this.loggerFactory.create(LOCAL_STORAGE_SERVICE);
  }

  /**
   * Save an item into localStorage
   * @param key - The key to save under
   * @param value - The value to save (object or string)
   */
  setItem<T>(key: string, value: T): void {
    if (!window.localStorage) {
      this.logger.log("[Set] localStorage is not available.");
      return;
    }

    try {
      const serializedValue =
        typeof value === "string" ? value : JSON.stringify(value);
      window.localStorage.setItem(key, serializedValue);
      this.logger.log(`[Set] localStorage: ${key}`, value);
    } catch (error) {
      this.logger.error(`[Set] Failed to set localStorage key: ${key}`, error);
    }
  }

  /**
   * Get an item from localStorage
   * @param key - The key to retrieve
   * @returns The value from localStorage, or null if not found
   */
  getItem<T>(key: string): T | null {
    if (!window.localStorage) {
      this.logger.log("[Get] localStorage is not available.");
      return null;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (!item) {
        this.logger.log("[Get] localStorage key not found");
        return null;
      }
      try {
        const parsedItem = JSON.parse(item);
        this.logger.log(`[Get] localStorage "${key}": `, parsedItem);

        return parsedItem as T;
      } catch {
        this.logger.log(`[Get] localStorage "${key}": `, item);

        return item as unknown as T;
      }
    } catch (error) {
      this.logger.error(`[Get] Failed to get localStorage key: ${key}`, error);
      return null;
    }
  }

  /**
   * Remove an item from localStorage
   * @param key - The key to remove
   */
  removeItem(key: string): void {
    if (!window.localStorage) {
      this.logger.log("[Remove] localStorage is not available.");
      return;
    }

    try {
      window.localStorage.removeItem(key);
      this.logger.log(`[Remove] localStorage key: ${key}`);
    } catch (error) {
      this.logger.error(
        `[Remove] Failed to remove localStorage key: ${key}`,
        error
      );
    }
  }

  /**
   * Clear all items from localStorage
   */
  clear(): void {
    if (!window.localStorage) {
      this.logger.log("[Clear] localStorage is not available.");
      return;
    }

    try {
      window.localStorage.clear();
      this.logger.log("[Clear] localStorage cleared");
    } catch (error) {
      this.logger.error("[Clear] Failed to clear localStorage", error);
    }
  }
}
